import { useLocation } from "react-router-dom";
import { iSharedCartSearch } from "../api/sharedCart/sharedCart";

// Query param that turns the EXISTING booking/review flow into a shared-cart
// open (no dedicated route — we reuse everything that already exists). The
// share link is `${APP_URL}?sharedCartId=<id>`.
export const SHARED_CART_QUERY_PARAM = "sharedCartId";

// sessionStorage mirror of "we are viewing a shared cart". The query param can
// be dropped by the desktop→mobile `/booking` bridge navigation, so the
// bootstrap also stamps this flag; the hide-logic (Modify / Share / Remove)
// reads it so it survives the whole flow until the cart is booked/abandoned.
const SHARED_CART_VIEW_FLAG = "_zenvoya_shared_cart_view";
// userId of the sharer (the SharedCart row's userId). Captured at bootstrap so
// the recipient's post-prebook itinerary save can stamp `sharedByUserId`.
const SHARED_BY_USER_ID = "_zenvoya_shared_by_user_id";
const SHARED_CART_SEARCH = "_zenvoya_shared_cart_search";

export function getSharedCartIdFromUrl(search?: string): string | null {
  try {
    return new URLSearchParams(search ?? window.location.search).get(SHARED_CART_QUERY_PARAM);
  } catch {
    return null;
  }
}

export function markSharedCartView() {
  try {
    sessionStorage.setItem(SHARED_CART_VIEW_FLAG, "1");
  } catch {
    // sessionStorage unavailable — best effort.
  }
}

export function clearSharedCartView() {
  try {
    sessionStorage.removeItem(SHARED_CART_VIEW_FLAG);
    sessionStorage.removeItem(SHARED_BY_USER_ID);
    sessionStorage.removeItem(SHARED_CART_SEARCH);
  } catch {
    // ignore
  }
}

export function setSharedCartSearch(search?: iSharedCartSearch) {
  try {
    if (search) sessionStorage.setItem(SHARED_CART_SEARCH, JSON.stringify(search));
    else sessionStorage.removeItem(SHARED_CART_SEARCH);
  } catch {}
}

export function getSharedCartSearch(): iSharedCartSearch | null {
  try {
    const stored = JSON.parse(sessionStorage.getItem(SHARED_CART_SEARCH) ?? "null");
    return stored && typeof stored === "object" ? stored : null;
  } catch {
    return null;
  }
}

// Sharer's userId — set at bootstrap, read by the post-prebook itinerary save.
export function setSharedByUserId(userId?: string) {
  try {
    if (userId) sessionStorage.setItem(SHARED_BY_USER_ID, userId);
  } catch {
    // ignore
  }
}

export function getSharedByUserId(): string | null {
  try {
    return sessionStorage.getItem(SHARED_BY_USER_ID);
  } catch {
    return null;
  }
}

function readSharedCartViewFlag(): boolean {
  try {
    return sessionStorage.getItem(SHARED_CART_VIEW_FLAG) === "1";
  } catch {
    return false;
  }
}

/**
 * True when the current session is viewing a shared cart — either the
 * `?sharedCartId=` param is on the URL (reactive via useLocation) or the
 * sessionStorage flag is set (survives the booking flow's internal navigation).
 * Used to hide Modify / Share / Remove for shared-link openers.
 */
export function useIsSharedCartView(): boolean {
  const location = useLocation();
  const hasParam = new URLSearchParams(location.search).has(SHARED_CART_QUERY_PARAM);
  return hasParam || readSharedCartViewFlag();
}
